import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect } from "react";
import axios from "axios";
import { AuthProvider } from "@/context/AuthContext";
import HomePage from "@/pages/HomePage";
import VehiclesPage from "@/pages/VehiclesPage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import AppointmentPage from "@/pages/AppointmentPage";
import ContactPage from "@/pages/ContactPage";
import ReviewsPage from "@/pages/ReviewsPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import TireMapEditor from "@/pages/TireMapEditor";
import TireMapView from "@/pages/TireMapView";
import ImpressumPage from "@/pages/ImpressumPage";
import DatenschutzPage from "@/pages/DatenschutzPage";
import AGBPage from "@/pages/AGBPage";
import CancelAppointmentPage from "@/pages/CancelAppointmentPage";
import PageEditorPage from "@/pages/PageEditorPage";
import VisualEditorPage from "@/pages/VisualEditorPage";
import { OverrideLayer } from "@/components/OverrideLayer";
import AktionenPage from "@/pages/AktionenPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import MeineGaragePage from "@/pages/MeineGaragePage";
import USBKeyAuth from "@/components/USBKeyAuth";
import MaintenanceMode from "@/components/MaintenanceMode";
import UpdateInProgress from "@/components/UpdateInProgress";
import ServerResting from "@/components/ServerResting";
import CustomPage from "@/pages/CustomPage";
import { EDIT_MODE } from "@/components/OverrideLayer";
import { CallbackPage } from "@/components/CallbackForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Wrapper component to handle maintenance mode and feature toggles
const AppContent = () => {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState({
    feature_fahrzeuge: true,
    feature_aktionen: true,
    feature_termine: true,
    feature_bewertungen: true,
    feature_kontakt: true,
    feature_reifenlager: true,
    feature_kundenportal: true
  });
  const location = useLocation();

  useEffect(() => {
    const checkSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data.updating) { setUpdating(true); return; }
        setUpdating((was) => { if (was) window.location.reload(); return false; });
        setPaused((was) => { if (was) window.location.reload(); return false; });
        setMaintenanceMode(response.data.maintenance_mode || false);
        setFeatures({
          feature_fahrzeuge: response.data.feature_fahrzeuge ?? true,
          feature_aktionen: response.data.feature_aktionen ?? true,
          feature_termine: response.data.feature_termine ?? true,
          feature_bewertungen: response.data.feature_bewertungen ?? true,
          feature_kontakt: response.data.feature_kontakt ?? true,
          feature_reifenlager: response.data.feature_reifenlager ?? true,
          feature_kundenportal: response.data.feature_kundenportal ?? true
        });
      } catch (error) {
        console.error("Error checking settings:", error);
        if (error.response?.data?.updating) { setUpdating(true); return; }
        if (error.response?.data?.paused) { setPaused(true); return; }
        setMaintenanceMode(false);
      } finally {
        setLoading(false);
      }
    };

    checkSettings();
    const interval = setInterval(checkSettings, updating ? 5000 : paused ? 30000 : 10000);
    return () => clearInterval(interval);
  }, [location.pathname, updating, paused]);

  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/tiremap');

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#EFDF00] border-t-transparent animate-spin rounded-full"></div>
      </div>
    );
  }

  if (updating) {
    return <UpdateInProgress />;
  }

  if (paused) {
    return <ServerResting />;
  }

  if (maintenanceMode && !isAdminRoute && !EDIT_MODE) {
    return <MaintenanceMode />;
  }

  return (
    <>
      <USBKeyAuth />
      {!isAdminRoute && <OverrideLayer />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        
        {/* Fahrzeuge - conditional */}
        <Route path="/fahrzeuge" element={
          features.feature_fahrzeuge ? <VehiclesPage /> : <Navigate to="/" replace />
        } />
        <Route path="/fahrzeuge/:id" element={
          features.feature_fahrzeuge ? <VehicleDetailPage /> : <Navigate to="/" replace />
        } />
        
        {/* Termine - conditional */}
        <Route path="/termin" element={
          features.feature_termine ? <AppointmentPage /> : <Navigate to="/" replace />
        } />
        
        {/* Kontakt - conditional */}
        <Route path="/kontakt" element={
          features.feature_kontakt ? <ContactPage /> : <CallbackPage />
        } />
        
        {/* Bewertungen - conditional */}
        <Route path="/bewertungen" element={
          features.feature_bewertungen ? <ReviewsPage /> : <Navigate to="/" replace />
        } />
        
        {/* Aktionen - conditional */}
        <Route path="/aktionen" element={
          features.feature_aktionen ? <AktionenPage /> : <Navigate to="/" replace />
        } />
        
        {/* Kundenportal - conditional */}
        <Route path="/login" element={
          features.feature_kundenportal ? <LoginPage /> : <Navigate to="/" replace />
        } />
        <Route path="/registrieren" element={
          features.feature_kundenportal ? <RegisterPage /> : <Navigate to="/" replace />
        } />
        <Route path="/meine-garage" element={
          features.feature_kundenportal ? <MeineGaragePage /> : <Navigate to="/" replace />
        } />
        
        {/* Reifenlager - conditional (public view) */}
        <Route path="/reifenlager" element={
          features.feature_reifenlager ? <TireMapView /> : <Navigate to="/" replace />
        } />
        
        {/* Static pages - always available */}
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />
        <Route path="/agb" element={<AGBPage />} />
        <Route path="/seite/:slug" element={<CustomPage />} />
        <Route path="/termin-absagen/:token" element={<CancelAppointmentPage />} />
        <Route path="/abbrechen/:token" element={<CancelAppointmentPage />} />
        
        {/* Admin routes - always available */}
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/editor" element={<VisualEditorPage />} />
        <Route path="/admin/editor/bereiche" element={<PageEditorPage />} />
        <Route path="/tiremap" element={<TireMapView />} />
        <Route path="/tiremap/editor" element={<TireMapEditor />} />
        
        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <AppContent />
        </div>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
